import {
  Contact,
  EventInput,
  ExternalPartner,
  LocationType,
  ShiftInput,
} from '@amzn/red-velvet-api';
import { TFunction } from 'i18next';
import { isValidEmail } from './validation';

export const GRANT_ID_REGEX = /^\d{4}VG-(AMER|APAC|EMEA)-\d{6}$/;
export const GRANT_ID_MAX_LENGTH = 30;

/** Returns true if the grant id is empty or matches the expected Benevity grant id format. */
export function isValidGrantIdFormat(grantId: string): boolean {
  return grantId === '' || GRANT_ID_REGEX.test(grantId);
}

export function validateEventDetails(eventInput: EventInput, t: TFunction): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!eventInput.title?.trim()) {
    errors.eventTitle = t('error.eventTitleRequired');
  }
  if (!eventInput.description?.trim()) {
    errors.eventDescription = t('error.eventDescriptionRequired');
  }
  if (!eventInput.shortDescription?.trim()) {
    errors.summarizedDescription = t('error.summarizedDescriptionRequired');
  }
  if (!eventInput.focusArea?.trim()) {
    errors.focusArea = t('error.focusAreaRequired');
  }
  if (!eventInput.primaryActivity?.trim()) {
    errors.primaryActivity = t('error.primaryActivityRequired');
  }
  if (eventInput.usedAciGrant === undefined) {
    errors.usedAciGrant = t('grant.questionRequired', { ns: 'translation' });
  }

  // Location-specific validation
  if (eventInput.location?.locationType === LocationType.ON_SITE) {
    if (!eventInput.location?.amazonBuilding?.trim()) {
      errors.building = t('error.buildingRequired');
    }
    if (!eventInput.location?.amazonBuildingRoom?.trim()) {
      errors.room = t('error.roomRequired');
    }
  } else if (eventInput.location?.locationType === LocationType.OFF_SITE) {
    if (!eventInput.location?.address_string?.trim()) {
      errors.location = t('error.locationRequired');
    }
    if (!eventInput.location?.name?.trim()) {
      errors.locationName = t('error.locationNameRequired');
    }
  }

  // ACI Grant specific validation
  if (eventInput.usedAciGrant === true) {
    if (!eventInput.grantId?.trim()) {
      errors.grantId = t('grant.idRequired', { ns: 'translation' });
    }
  }

  return errors;
}

export function validateShifts(shifts: ShiftInput[], t: TFunction): Record<string, string> {
  const errors: Record<string, string> = {};

  shifts.forEach((shift, index) => {
    const isScheduled = (shift.shiftTimingType ?? 'scheduled') === 'scheduled';

    if (!shift.startDateTime) {
      errors[`shift_${index}_date`] = t('error.shiftDateRequired');
      return;
    }

    if (isScheduled) {
      if (shift.endDateTime && shift.startDateTime.getTime() === shift.endDateTime.getTime()) {
        errors[`shift_${index}_startTime`] = t('error.shiftTimeRequired');
      } else if (shift.endDateTime && shift.endDateTime <= shift.startDateTime) {
        errors[`shift_${index}_endTime`] = t('error.shiftEndTimeBeforeStart');
      }
    } else {
      if (shift.endDateTime && shift.endDateTime <= shift.startDateTime) {
        const sameDay = shift.startDateTime.toDateString() === shift.endDateTime.toDateString();
        errors[sameDay ? `shift_${index}_endTime` : `shift_${index}_endDate`] = t(
          sameDay ? 'error.shiftEndTimeBeforeStart' : 'error.shiftEndDateBeforeStart'
        );
      }
      if (!shift.shiftLength || shift.shiftLength <= 0) {
        errors[`shift_${index}_shiftLength`] = t('error.shiftLengthRequired');
      } else if (shift.shiftLength > 720) {
        errors[`shift_${index}_shiftLength`] = t('error.shiftLengthExceeded');
      }
    }
  });

  return errors;
}

export function validateTeamAndPartnership(
  eventInput: EventInput,
  t: TFunction
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (eventInput.externalPlanningPartner === ExternalPartner.OTHER) {
    if (!eventInput.otherPartner?.trim()) {
      errors.otherPartnerName = t('error.otherPartnerRequired');
    } else if (eventInput.otherPartner.length > 30) {
      errors.otherPartnerName = t('error.inputTooLong', { maxLength: 30 });
    }
  }

  const validateContacts = (
    contacts: Contact[],
    prefix: string,
    opts: {
      requireName: boolean;
      requireEmail: boolean;
      requirePhone: boolean;
      nameErrorKey: string;
    }
  ) => {
    contacts.forEach((contact, i) => {
      if (opts.requireName && !contact.name?.trim() && !contact.alias?.trim()) {
        errors[`${prefix}_${i}_name`] = t(opts.nameErrorKey);
      }
      if (opts.requireEmail && !contact.email?.trim()) {
        errors[`${prefix}_${i}_email`] = t('error.contactEmailRequired');
      } else if (contact.email && !isValidEmail(contact.email)) {
        errors[`${prefix}_${i}_email`] = t('error.invalidEmail');
      }
      if (opts.requirePhone && !contact.phone?.trim()) {
        errors[`${prefix}_${i}_phone`] = t('error.contactPhoneRequired');
      }
    });
  };

  validateContacts(eventInput.amazonContacts ?? [], 'amazonContact', {
    requireName: true,
    requireEmail: false,
    requirePhone: false,
    nameErrorKey: 'error.contactNameRequired',
  });
  validateContacts(eventInput.externalContacts ?? [], 'externalContact', {
    requireName: true,
    requireEmail: true,
    requirePhone: false,
    nameErrorKey: 'error.contactNameEmpty',
  });

  return errors;
}
